import { Component, type ErrorInfo, type ReactNode } from "react";
import { Wordmark } from "./Wordmark";
import { logger } from "../../services/telemetry";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/* Top-level safety net. Without it an unexpected render error blanks the whole
 * app; here it degrades to a branded, recoverable message and logs the error
 * for diagnostics. Wraps the entire app in App.tsx. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // logger.error also console.errors, so a render crash is both visible in
    // the browser console and shipped to OTEL (Loki) with the component stack.
    logger.error(error, "dashboard render crashed", {
      componentStack: info.componentStack ?? "",
    });
  }

  render() {
    if (!this.state.error) return this.props.children;

    // Mirrors the AppLoadingScreen splash ([Gateway] wordmark, tokens with
    // light-mode fallbacks) so a crash reads as the same surface the app
    // booted from — self-contained, since theme context may not exist here.
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{
          background: "var(--page, #FBFBFB)",
          fontFamily:
            "'Söhne','Inter','DM Sans',ui-sans-serif,system-ui,sans-serif",
        }}
      >
        <div className="flex flex-1 flex-col items-center justify-center gap-8 px-6 text-center">
          <Wordmark
            height={26}
            label="Gateway"
            color="var(--text, #140309)"
            bracket="var(--text-muted, #827C7E)"
          />
          <div className="flex flex-col items-center gap-3">
            <h1
              className="text-[20px] font-medium leading-tight"
              style={{ color: "var(--text, #140309)" }}
            >
              Something went wrong
            </h1>
            <p
              className="max-w-[400px] text-[13px] leading-relaxed"
              style={{ color: "var(--text-muted, #827C7E)" }}
            >
              The dashboard hit an unexpected error. Reloading usually clears
              it; if it persists, the analytics service may be unavailable.
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="rounded-[2px] px-[15px] py-[9px] text-[13px] text-white transition-opacity hover:opacity-90"
            style={{ background: "#9D1D48" }}
          >
            Reload dashboard
          </button>
        </div>
      </div>
    );
  }
}
